class IsaAboutCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.images = [...this.querySelectorAll('[data-carousel-image]')];
    this.panels = [...this.querySelectorAll('[data-carousel-panel]')];
    this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
    this.slideCount = Math.min(this.images.length, this.panels.length);
    this.currentIndex = 0;
    this.delay = Math.max(3000, Number(this.dataset.delay) || 4000);
    this.autoplay = this.dataset.autoplay === 'true';
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.initialized = true;

    if (this.slideCount < 2) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.show(index);
        this.restart();
      });

      dot.addEventListener('keydown', (event) => this.handleDotKeydown(event));
    });

    this.addEventListener('focusin', () => {
      this.focusInside = true;
      this.stop();
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.focusInside = false;
        this.start();
      }
    });

    this.addEventListener('touchstart', (event) => {
      this.touchStartX = event.changedTouches[0]?.clientX;
    }, { passive: true });

    this.addEventListener('touchend', (event) => {
      if (typeof this.touchStartX !== 'number') return;
      const distance = (event.changedTouches[0]?.clientX || 0) - this.touchStartX;
      this.touchStartX = null;

      if (Math.abs(distance) < 48) return;
      this.show(this.currentIndex + (distance < 0 ? 1 : -1));
      this.restart();
    }, { passive: true });

    document.addEventListener('visibilitychange', this.handleVisibilityChange = () => {
      if (document.hidden) this.stop();
      else this.start();
    });

    this.show(0, false);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  show(index, moveFocus = false) {
    const nextIndex = (index + this.slideCount) % this.slideCount;
    this.currentIndex = nextIndex;

    this.images.forEach((image, imageIndex) => {
      const active = imageIndex === nextIndex;
      image.classList.toggle('is-active', active);
      image.setAttribute('aria-hidden', String(!active));
    });

    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === nextIndex;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', String(!active));
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === nextIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
      dot.tabIndex = active ? 0 : -1;
    });

    if (moveFocus) this.dots[nextIndex]?.focus();
  }

  handleDotKeydown(event) {
    let nextIndex = null;

    if (event.key === 'ArrowRight') nextIndex = this.currentIndex + 1;
    if (event.key === 'ArrowLeft') nextIndex = this.currentIndex - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.slideCount - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    this.show(nextIndex, true);
    this.restart();
  }

  start() {
    if (
      !this.autoplay ||
      this.reduceMotion ||
      this.slideCount < 2 ||
      document.hidden ||
      this.focusInside
    ) return;
    this.stop();
    this.timer = window.setInterval(() => this.show(this.currentIndex + 1), this.delay);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('isa-about-carousel')) {
  customElements.define('isa-about-carousel', IsaAboutCarousel);
}
